import { Box, SpeedDial, SpeedDialAction, SpeedDialIcon, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { lazy, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Theme } from '@mui/material/styles';

import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { FEATURE_FLAGS } from '@/common/domain/constants/featureFlags';
import Groups2Icon from '@mui/icons-material/Groups2';
import MapsHomeWorkIcon from '@mui/icons-material/MapsHomeWork';
import WorkIcon from '@mui/icons-material/Work';
import { useFeatureFlag } from '@/common/presentation/hooks/useFeatureFlag';
import { useShiftCHotkey } from '@/common/presentation/hooks/useShiftCHotkey';
import { useShiftDHotkey } from '@/common/presentation/hooks/useShiftDHotkey';
import { useShiftHHotkey } from '@/common/presentation/hooks/useShiftHHotkey';
import { useGlobalFAB } from './GlobalFABContext';
import JobDrawer from '@/jobs/presentation/pages/JobsPage/JobDrawer';
import JobSiteModal from '@/jobSites/presentation/pages/JobSitesPage/JobSitesManagementTab/JobSiteModal';
import tid from '@/common/infrastructure/config/tid';

const CustomerModal = lazy(() => import('@/customers/presentation/pages/CustomersPage/CustomersManagementTab/CustomerModal'));

type ActionKey = 'job' | 'customer' | 'jobSite';

interface ActionConfig {
  key: ActionKey;
  icon: React.ReactNode;
  tooltipTitle: string;
  route: string;
}

const GlobalFABContent = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isJobDrawerOpen, setIsJobDrawerOpen] = useState(false);
  const [isJobSiteModalOpen, setIsJobSiteModalOpen] = useState(false);
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
  const navigate = useNavigate();
  const showFloatingActionButton = useFeatureFlag(FEATURE_FLAGS.SHOW_FLOATING_ACTION_BUTTON);
  const useNewModal = useFeatureFlag(FEATURE_FLAGS.JOBS_ERP_CREATE_MODAL_NEW_LOOK_AND_FEEL);
  const areHotkeysEnabled = useFeatureFlag(FEATURE_FLAGS.ENABLE_HOTKEYS);

  const handleMainFabClick = () => {
    setIsExpanded((prev) => !prev);
  };

  const { isVisible, setIsVisible } = useGlobalFAB();

  const handleShowFAB = () => {
    setIsVisible(true);
  };

  const handleHideFAB = () => {
    setIsVisible(false);
  };

  const handleCloseJobDrawer = () => {
    setIsJobDrawerOpen(false);
  };

  const handleCloseJobSiteModal = () => {
    setIsJobSiteModalOpen(false);
  };

  const handleOpenCustomerModal = () => {
    setIsCustomerModalOpen(true);
  };

  const handleCloseCustomerModal = () => {
    setIsCustomerModalOpen(false);
  };

  // Action handlers map
  const actionHandlers = {
    job: () => {
      if (useNewModal) {
        setIsJobDrawerOpen(true);
      } else {
        navigate('/jobs/create');
      }
    },
    jobSite: () => {
      setIsJobSiteModalOpen(true);
    },
    customer: () => {
      handleOpenCustomerModal();
    }
  };

  const handleActionClick = (route: string, actionKey?: ActionKey) => {
    setIsExpanded(false);

    if (actionKey && actionHandlers[actionKey]) {
      actionHandlers[actionKey]();
    } else {
      navigate(route);
    }
  };

  // Extract reusable styles to eliminate duplication
  const actionStyles = {
    fab: {
      width: 56,
      height: 56,
      backgroundColor: 'common.white',
      border: '2px solid',
      borderColor: 'primary.main',
      '&:hover': {
        backgroundColor: 'grey.50'
      },
      '&:focus': {
        backgroundColor: 'common.white',
        background: (theme: Theme) =>
          `linear-gradient(0deg, ${alpha(theme.palette.primary.main, 0.3)} 0%, ${alpha(theme.palette.primary.main, 0.3)} 100%), ${theme.palette.common.white}`,
        borderColor: 'primary.main'
      }
    },
    staticTooltip: {
      backgroundColor: 'common.white',
      width: '300px',
      color: 'primary.main',
      border: '2px solid',
      borderColor: 'primary.light',
      borderRadius: '8px',
      fontWeight: 'bold',
      fontSize: '12px',
      textTransform: 'uppercase'
    },
    staticTooltipLabel: {
      px: 1.5,
      py: 0,
      fontSize: '12px',
      borderRadius: '10px',
      fontWeight: 'bold',
      width: 'max-content',
      textTransform: 'uppercase',
      color: 'primary.main',
      cursor: 'pointer',
      mr: 0,
      boxShadow: 6
    }
  };

  // Configuration array for actions to eliminate repetition
  const actions: ActionConfig[] = [
    {
      key: 'job',
      icon: <WorkIcon color="primary" />,
      tooltipTitle: 'NEW JOB',
      route: '/jobs/create'
    },
    {
      key: 'customer',
      icon: <Groups2Icon color="primary" />,
      tooltipTitle: 'NEW CUSTOMER',
      route: '/customers/create'
    },
    {
      key: 'jobSite',
      icon: <MapsHomeWorkIcon color="primary" />,
      tooltipTitle: 'CREATE JOBSITE',
      route: '/jobsites/create'
    }
  ];

  // Disable hotkeys when user is typing in form fields to prevent accidental FAB opening
  useShiftCHotkey(handleMainFabClick, true, false);
  useShiftDHotkey(handleShowFAB, true, false);
  useShiftHHotkey(handleHideFAB, true, false);

  const mainFabTooltipTitle = areHotkeysEnabled ? (
    <Box component="span" sx={{ display: 'block', maxWidth: 280, textAlign: 'left', py: 0.25 }}>
      <Typography component="span" variant="body2" sx={{ display: 'block', fontWeight: 600 }}>
        Open or close quick actions
      </Typography>
      <Typography component="span" variant="caption" sx={{ display: 'block', mt: 0.75, opacity: 0.92, lineHeight: 1.45 }}>
        Click the button, or press Shift+C to toggle the menu.
        <br />
        Press Shift+D to show this button, Shift+H to hide it.
      </Typography>
    </Box>
  ) : (
    'Open or close quick actions (click the button).'
  );

  if (!showFloatingActionButton) {
    return null;
  }

  return (
    <Box
      aria-hidden={!isVisible}
      sx={(theme) => ({
        position: 'fixed',
        bottom: 20,
        right: 20,
        zIndex: theme.zIndex.speedDial,
        display: isVisible ? 'block' : 'none',
        pointerEvents: isVisible ? 'auto' : 'none'
      })}
    >
      <Tooltip
        describeChild
        placement="left"
        enterDelay={400}
        title={mainFabTooltipTitle}
        slotProps={{ popper: { sx: { '& .MuiTooltip-tooltip': { maxWidth: 320 } } } }}
        {...tid('common:global-fab:main-tooltip')}
      >
        <Box component="span" sx={{ display: 'inline-flex', verticalAlign: 'middle' }}>
          <SpeedDial
            ariaLabel="Global actions"
            sx={{
              '& .MuiSpeedDial-actions': {
                height: isExpanded ? 'auto' : 0
              },
              position: 'relative',
              '& .MuiFab-primary': {
                width: 56,
                height: 56,
                backgroundColor: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.dark'
                }
              }
            }}
            icon={<SpeedDialIcon icon={<AddIcon />} openIcon={<CloseIcon />} />}
            onClick={handleMainFabClick}
            open={isExpanded}
            direction="up"
            {...tid('common:global-fab:main-button')}
          >
            {actions.map((action) => (
              <SpeedDialAction
                key={action.key}
                icon={action.icon}
                tooltipOpen
                tooltipTitle={action.tooltipTitle}
                onClick={() => handleActionClick(action.route, action.key)}
                sx={{
                  '& .MuiSpeedDialAction-fab': actionStyles.fab,
                  '& .MuiSpeedDialAction-staticTooltip': actionStyles.staticTooltip,
                  '& .MuiSpeedDialAction-staticTooltipLabel': actionStyles.staticTooltipLabel
                }}
                {...tid(`common:global-fab:action-button-${action.key}`)}
              />
            ))}
          </SpeedDial>
        </Box>
      </Tooltip>

      {isJobDrawerOpen && <JobDrawer open={isJobDrawerOpen} onClose={handleCloseJobDrawer} />}

      {isJobSiteModalOpen && (
        <JobSiteModal open={isJobSiteModalOpen} onClose={handleCloseJobSiteModal} onInputActionClicked={handleCloseJobSiteModal} />
      )}

      {isCustomerModalOpen && <CustomerModal open={isCustomerModalOpen} onClose={handleCloseCustomerModal} />}
    </Box>
  );
};

const GlobalFAB = () => {
  return <GlobalFABContent />;
};

export default GlobalFAB;
